import { FC, ReactNode, Ref, useId } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { TooltipGroup, TooltipGroupAnchor } from '@/components/shared/tooltip/TooltipGroup';
import { EditIcon } from '@/components/base/Icons/RocheIconPack';
import IconButton from '@/storybook/components/IconButton/IconButton';
import Text from '@/storybook/components/Text/Text';
import AILogoGradientIcon from '@/storybook/icons/AILogoGradientIcon';
import ArrowLeftIcon from '@/storybook/icons/ArrowLeftIcon';
import DoubleChevronRightIcon from '@/storybook/icons/DoubleChevronRightIcon';
import HistoryIcon from '@/storybook/icons/HistoryIcon';
import { TestIds } from './testIds';
import styles from './ChatHeader.module.css';

export interface ChatHeaderProps {
  onClose: VoidFunction;
  backButton?: boolean;
  onHistoryClick?: VoidFunction;
  historyButtonRef?: Ref<HTMLButtonElement>;
  onNewChat: VoidFunction;
  newChatDisabled?: boolean;
  showHistory?: boolean;
  historyDisabled?: boolean;
  historyHidden?: boolean;
  showNewChat?: boolean;
  showTitle?: boolean;
  title?: ReactNode;
}

const ChatHeader: FC<ChatHeaderProps> = ({
  onClose,
  backButton = false,
  onHistoryClick,
  historyButtonRef,
  onNewChat,
  newChatDisabled = false,
  showHistory = true,
  historyDisabled = false,
  historyHidden = false,
  showNewChat = true,
  showTitle = true,
  title,
}) => {
  const { t } = useTranslation();
  const tooltipGroupId = useId();

  return (
    <TooltipGroup id={`ai-chat-header-${tooltipGroupId}`} shouldShowForTruncatedOnly={false} delayShow={200}>
      <header className={styles.header} data-testid={TestIds.header}>
        <div className={styles.left}>
          <IconButton
            appearance="text"
            size="M"
            icon={
              backButton ? (
                <ArrowLeftIcon className={styles.actionIcon} />
              ) : (
                <DoubleChevronRightIcon className={styles.actionIcon} />
              )
            }
            onClick={onClose}
            aria-label={t(
              backButton
                ? 'components.aiChatDrawer.history.backAriaLabel'
                : 'components.aiChatDrawer.header.closeAriaLabel',
            )}
            className={styles.actionButton}
            data-testid={TestIds.closeButton}
          />
        </div>

        {showTitle && (
          <div className={styles.center} data-testid={TestIds.titleSection}>
            <AILogoGradientIcon className={styles.aiIcon} />
            <div className={styles.titleGroup}>
              <Text as="span" className={styles.title}>
                {title ?? t('components.aiChatDrawer.header.title')}
              </Text>
            </div>
          </div>
        )}

        <div className={styles.right}>
          {showHistory && (
            <TooltipGroupAnchor
              value={t('components.aiChatDrawer.header.historyTooltip')}
              place="bottom"
              forceDisplayTooltip
            >
              <span className={styles.actionTooltipAnchor}>
                <IconButton
                  ref={historyButtonRef}
                  appearance="text"
                  size="M"
                  icon={<HistoryIcon className={styles.actionIcon} />}
                  onClick={onHistoryClick}
                  disabled={historyDisabled}
                  aria-label={t('components.aiChatDrawer.header.historyAriaLabel')}
                  className={styles.actionButton}
                  data-testid={TestIds.historyButton}
                />
              </span>
            </TooltipGroupAnchor>
          )}
          {historyHidden && (
            <IconButton
              appearance="text"
              size="M"
              icon={<HistoryIcon className={styles.actionIcon} />}
              onClick={onHistoryClick}
              disabled
              aria-label={t('components.aiChatDrawer.header.historyAriaLabel')}
              className={cx(styles.actionButton, styles.actionHidden)}
              data-testid={TestIds.historyButton}
            />
          )}
          {showNewChat && (
            <TooltipGroupAnchor
              value={t('components.aiChatDrawer.header.newChatTooltip')}
              place="bottom"
              forceDisplayTooltip
            >
              <span className={styles.actionTooltipAnchor}>
                <IconButton
                  appearance="text"
                  size="M"
                  icon={<EditIcon className={styles.actionIcon} />}
                  onClick={onNewChat}
                  disabled={newChatDisabled}
                  aria-label={t('components.aiChatDrawer.header.newChatAriaLabel')}
                  className={cx(styles.actionButton, styles.newChatButton)}
                  data-testid={TestIds.newChatButton}
                />
              </span>
            </TooltipGroupAnchor>
          )}
        </div>
      </header>
    </TooltipGroup>
  );
};

export default ChatHeader;
